import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { PhysicianModalComponent } from 'app/layout/physician-modal/physician-modal.component';
import { lastValueFrom } from 'rxjs';
import { AuthenticationService } from './authentication.service';
import { Role } from './enums/role.enum';
import { CurrentUser } from './models/current-user';
import { PhysicianUser } from './models/physician-user';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  constructor(
    private _authService: AuthenticationService,
    private _router: Router,
    private _dialog: MatDialog,
    @Inject(PLATFORM_ID) private _platformId: Object
  ) {}

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    if (isPlatformBrowser(this._platformId)) {
      if (state.url === '/logout') {
        this._authService.clearUserData();
        this._router.navigate(['/']);
        return false;
      }

      const currentUser = await lastValueFrom(this._authService.getUser());
      const notAuthorized = this.isNotAuthorized(currentUser, route.data.roles);
      const navigateToDefaultUrl = currentUser && route.routeConfig.path === '';

      if (currentUser instanceof PhysicianUser) {
        this.openPhysicianModal(currentUser);
        return false;
      }

      if (notAuthorized || navigateToDefaultUrl) {
        this._router.navigate([currentUser ? currentUser.defaultUrl : '/']);
        return false;
      }
      return true;
    }
    return true;
  }

  private isNotAuthorized(currentUser: CurrentUser, roles: Role[]) {
    return !(roles.indexOf(Role.ALL) !== -1 || (currentUser && roles.indexOf(currentUser.role) !== -1));
  }

  private openPhysicianModal(currentUser: CurrentUser) {
    this._authService.clearUserData();
    this._router.navigate(['/']);
    this._dialog
      .open(PhysicianModalComponent, {
        width: '600px',
        backdropClass: 'backdrop-background',
        autoFocus: false,
      })
      .afterClosed()
      .subscribe();
  }
}
